// Modules
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
// Store
import { RootState } from "@/config/store";
// Actions
import { getPatientEventsSWR, PatientSubstitutesSWR } from "@/actions/patient/calendarActions";
import { showModal } from "@/config/store/modalReducer";
// Helpers
import { getUserData } from "@/helpers/userData";
import { isEligibleForFeedback } from "@/helpers/patient";

export function useModalEligibility() {
    const dispatch = useDispatch();
    const patient = getUserData();
    const patientId = patient?._id ?? "";
    const therapyProgram = patient?.therapy_program;

    const { isOpen } = useSelector((state: RootState) => state.modal);

    const { isSubstituteEligible, isLoading: isLoadingSubstitute } = PatientSubstitutesSWR(patientId);
    const { events } = getPatientEventsSWR(patientId);

    const isFeedbackEligible = isEligibleForFeedback({
        events,
        feedback: therapyProgram?.feedback,
        therapyStartDate: therapyProgram?.session?.start_date
    });

    const hasShownSubstitute = useRef(false);
    const hasShownFeedback = useRef(false);
    const pendingModal = useRef<"feedback" | null>(null);

    useEffect(() => {
        if (!patient || patient.role !== "user") return;
        if (isLoadingSubstitute) return;

        if (!isOpen) {
            if (pendingModal.current) {
                dispatch(showModal(pendingModal.current));
                if (pendingModal.current === "feedback") {
                    hasShownFeedback.current = true;
                }
                pendingModal.current = null;
            } else {
                if (isSubstituteEligible && !hasShownSubstitute.current) {
                    dispatch(showModal("substitute"));
                    hasShownSubstitute.current = true;

                    if (isFeedbackEligible && !hasShownFeedback.current) {
                        pendingModal.current = "feedback";
                    }
                } else if (isFeedbackEligible && !hasShownFeedback.current) {
                    dispatch(showModal("feedback"));
                    hasShownFeedback.current = true;
                }
            }
        }
    }, [isSubstituteEligible, isFeedbackEligible, isLoadingSubstitute, isOpen, dispatch, patient]);
}
